import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import GuardianAuth from '@amzn/GuardianAuth/lib/auth/GuardianAuth';
import ToastErrorMessage from '@amzn/GuardianWidgetCommons/lib/components/ToastErrorMessage';
import asyncRequest from '@amzn/GuardianWidgetCommons/lib/helper/asyncRequest';
import React from 'react';
import { toast } from 'react-toastify';
import { AppThunk } from '../../context/store';
import { GET_TASKLISTS_API_ENDPOINT } from '../../lib/globals';
import { Logger } from '../../lib/utils/logger';
import { createCSV, ColumnProps } from '../../lib/helpers/CSVHelper';
import { durationSecondsToString, formatToLocalTime, toPct2dp } from '../../lib/helpers/helpers';
import { DateTime } from 'luxon';
import { TasklistsPropMap, TasklistsTitlePropMap } from '../../lib/dictionaries/TasklistsDictionary';
import {
    TasklistFilters,
    TasklistRow,
    TasklistSortBy,
    TasklistSortDir,
    TasklistSortOrder,
    TasklistStatus,
    TasklistStatusCounts
} from '../../lib/types';

const { doRequest } = asyncRequest;

interface TasklistsState {
    tasklists: TasklistRow[];
    totalCount: number;
    pageSize: number;
    pageNumber: number;
    filters: TasklistFilters | null;
    sort: TasklistSortOrder;
    isGettingTasklists: boolean;
    getTasklistsError: string;
    oneLiner: string;
    lastDataRefreshAt: string;
    statusCounts: TasklistStatusCounts | null;
}

interface TasklistsStatePayload {
    tasklists: TasklistRow[];
    totalCount: number;
    pageSize: number;
    pageNumber: number;
    filters: TasklistFilters | null;
    sort: TasklistSortOrder;
    oneLiner: string;
    lastDataRefreshAt: string;
    statusCounts: TasklistStatusCounts | null;
}

export const initialState: TasklistsState = {
    tasklists: [],
    totalCount: 0,
    pageSize: 50,
    pageNumber: 1,
    filters: null,
    sort: { column: 'start', order: 'desc' },
    isGettingTasklists: false,
    getTasklistsError: '',
    oneLiner: '',
    lastDataRefreshAt: '',
    statusCounts: null
};

const tasklistsSlice = createSlice({
    name: 'tasklists',
    initialState,
    reducers: {
        startGetTasklists: (state) => {
            state.tasklists = initialState.tasklists;
            state.getTasklistsError = initialState.getTasklistsError;
            state.isGettingTasklists = true;
        },
        getTasklistsSucceeded: (state, { payload }: PayloadAction<TasklistsStatePayload>) => {
            const {
                tasklists,
                totalCount,
                pageSize,
                pageNumber,
                filters,
                sort,
                oneLiner,
                lastDataRefreshAt,
                statusCounts
            } = payload;

            state.tasklists = tasklists;
            state.totalCount = totalCount;
            state.pageSize = pageSize;
            state.pageNumber = pageNumber;
            state.filters = filters;
            state.sort = sort;
            state.statusCounts = statusCounts;
            state.isGettingTasklists = false;
            state.getTasklistsError = '';
            state.oneLiner = oneLiner;
            state.lastDataRefreshAt = lastDataRefreshAt;
        },
        getTasklistsFailed: (state, { payload }: PayloadAction<{ error: string }>) => {
            state.isGettingTasklists = false;
            state.getTasklistsError = payload.error;
        }
    }
});

export const { startGetTasklists, getTasklistsSucceeded, getTasklistsFailed } = tasklistsSlice.actions;

export default tasklistsSlice.reducer;

interface GetTasklistsHelperParams {
    pageNumber: number;
    pageSize: number;
    sortBy: TasklistSortBy;
    sortDir: TasklistSortDir;
    filters: TasklistFilters | null;
}

interface GetTasklistsHelperResult {
    totalCount: number;
    tasklists: TasklistRow[];
    oneLiner: string;
    lastDataRefreshAt: string;
    statusCounts: TasklistStatusCounts | null;
}

const getTasklistsHelper = async ({
    pageNumber,
    pageSize,
    sortBy,
    sortDir,
    filters
}: GetTasklistsHelperParams): Promise<GetTasklistsHelperResult> => {
    const requestBody = {
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        filters: filters ?? {}
    };

    const response = await doRequest(
        GET_TASKLISTS_API_ENDPOINT,
        requestBody,
        GuardianAuth.createRequestAuthHeader()
    );

    let data: any = response?.data ?? {};
    if (data?.body) {
        try {
            data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        } catch {
            // ignore parse failure
        }
    }

    const totalCount: number = data?.page?.total ?? 0;

    const rows: TasklistRow[] = (data?.rows ?? []).map((row: any) => ({
        tasklistId: row.tasklistId,
        largeScaleEventName: row.largeScaleEventName ?? null,
        eventName: row.eventName ?? null,
        title: row.title ?? null,
        country: row.country ?? null,
        state: row.state ?? null,
        city: row.city ?? null,
        siteCode: row.siteCode ?? null,
        siteId: row.siteId ?? null,
        facilityType: row.facilityType ?? null,
        businessLine: row.businessLine ?? null,
        gsoCluster: row.gsoCluster ?? null,
        numTasks: row.numTasks ?? 0,
        numCompleted: row.numCompleted ?? 0,
        completionRate: row.completionRate ?? 0,
        status: row.status as TasklistStatus,
        start: row.start ?? null,
        end: row.end ?? null,
        reasons: row.reasons ?? null,
        durationSeconds: row.durationSeconds ?? null,
        lastUpdateTime: row.lastUpdateTime ?? null
    }));

    const statusCounts: TasklistStatusCounts | null =
        (data?.metrics?.statusCounts as TasklistStatusCounts | undefined) ?? null;

    const oneLiner: string = data?.metrics?.oneLiner ?? '';
    const lastDataRefreshAt: string = data?.timestamps?.lastDataRefreshAt ?? '';

    return { totalCount, tasklists: rows, oneLiner, lastDataRefreshAt, statusCounts };
};

export const getTasklistsAsync = (
    pageNumber: number,
    pageSize: number,
    filters: TasklistFilters | null,
    sort: TasklistSortOrder
): AppThunk => async (dispatch) => {
    dispatch(startGetTasklists());
    try {
        const { statusIn, ...baseFilters } = filters || {};

        const [mainResult, statusCountsResult] = await Promise.allSettled([
            getTasklistsHelper({
                pageNumber,
                pageSize,
                sortBy: sort.column,
                sortDir: sort.order,
                filters
            }),
            getTasklistsHelper({
                pageNumber: 1,
                pageSize: 1,
                sortBy: sort.column,
                sortDir: sort.order,
                filters: baseFilters
            })
        ]);

        if (mainResult.status !== 'fulfilled') {
            throw mainResult.reason;
        }

        const { totalCount, tasklists, oneLiner, lastDataRefreshAt } = mainResult.value;
        const statusCounts =
            statusCountsResult.status === 'fulfilled' ? statusCountsResult.value.statusCounts ?? null : null;

        dispatch(
            getTasklistsSucceeded({
                totalCount,
                tasklists,
                pageNumber,
                pageSize,
                filters,
                sort,
                oneLiner,
                lastDataRefreshAt,
                statusCounts
            })
        );
    } catch (error) {
        let message = error?.message ?? 'Unknown error';
        const { response } = error || {};
        if (response?.data?.body) {
            try {
                const parsedBody =
                    typeof response.data.body === 'string'
                        ? JSON.parse(response.data.body)
                        : response.data.body;
                message = parsedBody?.message || parsedBody?.error || message;
            } catch {
                // ignore parse failure
            }
        }
        dispatch(getTasklistsFailed({ error: message }));
        toast.error(<ToastErrorMessage header={'Get tasklists failed'} errorMessage={message} />);
        Logger.error(`Get tasklists failed: ${message}`);
    }
};

export const downloadTasklistsCSVAsync = (
    filename: string,
    filters: TasklistFilters | null,
    sort: TasklistSortOrder
): AppThunk => async () => {
    const toastId = toast.info('Starting to download Tasklists CSV');
    try {
        const pageSize = 200;
        let pageNumber = 1;
        let totalCount = -1;
        const allRows: TasklistRow[] = [];

        do {
            const { tasklists, totalCount: returnedTotal } = await getTasklistsHelper({
                pageNumber,
                pageSize,
                sortBy: sort.column,
                sortDir: sort.order,
                filters
            });
            if (totalCount < 0) {
                totalCount = returnedTotal;
            }
            if (tasklists.length === 0) {
                break;
            }
            allRows.push(...tasklists);
            pageNumber += 1;
        } while (allRows.length < totalCount);

        const formattedRows = allRows.map((r) => {
            const row = { ...r } as any;

            if (typeof row.durationSeconds === 'number') {
                row.durationSeconds = durationSecondsToString(row.durationSeconds);
            }
            if (row.start) {
                row.start = formatToLocalTime(DateTime.fromISO(row.start));
            }
            if (row.end) {
                row.end = formatToLocalTime(DateTime.fromISO(row.end));
            }
            if (row.lastUpdateTime) {
                row.lastUpdateTime = formatToLocalTime(DateTime.fromISO(row.lastUpdateTime));
            }
            if (typeof row.completionRate === 'number') {
                row.completionRate = `${toPct2dp(row.completionRate)}%`;
            }

            return row;
        });

        const normalizedRows = formattedRows.map((row) =>
            Object.fromEntries(
                Object.entries(row).map(([column, rawValue]) => {
                    let value = rawValue as any;

                    const isBlank =
                        value === null ||
                        value === undefined ||
                        (typeof value === 'string' && value.trim() === '') ||
                        (Array.isArray(value) && value.length === 0) ||
                        (typeof value === 'number' && Number.isNaN(value));

                    if (isBlank) {
                        value = 'N/A';
                    } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
                        value = Array.isArray(value) ? value.join(', ') : JSON.stringify(value);
                        if (typeof value === 'string' && value.trim() === '') {
                            value = 'N/A';
                        }
                    }

                    return [column, value];
                })
            )
        );

        const columnProps: ColumnProps = {
            columns: [
                TasklistsTitlePropMap.largeScaleEventName,
                TasklistsTitlePropMap.eventName,
                TasklistsTitlePropMap.title,
                TasklistsTitlePropMap.country,
                TasklistsTitlePropMap.state,
                TasklistsTitlePropMap.city,
                TasklistsTitlePropMap.siteCode,
                TasklistsTitlePropMap.facilityType,
                TasklistsTitlePropMap.businessLine,
                TasklistsTitlePropMap.gsoCluster,
                TasklistsTitlePropMap.numTasks,
                TasklistsTitlePropMap.numCompleted,
                TasklistsTitlePropMap.completionRate,
                TasklistsTitlePropMap.status,
                TasklistsTitlePropMap.start,
                TasklistsTitlePropMap.end,
                TasklistsTitlePropMap.reasons,
                TasklistsTitlePropMap.durationSeconds,
                TasklistsTitlePropMap.lastUpdateTime
            ],
            columnDataMap: {
                [TasklistsTitlePropMap.largeScaleEventName]: TasklistsPropMap.largeScaleEventName,
                [TasklistsTitlePropMap.eventName]: TasklistsPropMap.eventName,
                [TasklistsTitlePropMap.title]: TasklistsPropMap.title,
                [TasklistsTitlePropMap.country]: TasklistsPropMap.country,
                [TasklistsTitlePropMap.state]: TasklistsPropMap.state,
                [TasklistsTitlePropMap.city]: TasklistsPropMap.city,
                [TasklistsTitlePropMap.siteCode]: TasklistsPropMap.siteCode,
                [TasklistsTitlePropMap.facilityType]: TasklistsPropMap.facilityType,
                [TasklistsTitlePropMap.businessLine]: TasklistsPropMap.businessLine,
                [TasklistsTitlePropMap.gsoCluster]: TasklistsPropMap.gsoCluster,
                [TasklistsTitlePropMap.numTasks]: TasklistsPropMap.numTasks,
                [TasklistsTitlePropMap.numCompleted]: TasklistsPropMap.numCompleted,
                [TasklistsTitlePropMap.completionRate]: TasklistsPropMap.completionRate,
                [TasklistsTitlePropMap.status]: TasklistsPropMap.status,
                [TasklistsTitlePropMap.start]: TasklistsPropMap.start,
                [TasklistsTitlePropMap.end]: TasklistsPropMap.end,
                [TasklistsTitlePropMap.reasons]: TasklistsPropMap.reasons,
                [TasklistsTitlePropMap.durationSeconds]: TasklistsPropMap.durationSeconds,
                [TasklistsTitlePropMap.lastUpdateTime]: TasklistsPropMap.lastUpdateTime
            }
        };

        const csv = await createCSV(normalizedRows, columnProps);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

        if ((navigator as any).msSaveBlob) {
            (navigator as any).msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('visibility', 'hidden');
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        toast.update(toastId, { render: 'Downloaded Tasklists CSV Successfully', type: toast.TYPE.SUCCESS });
        Logger.info(`Downloaded Tasklists CSV Successfully`);
    } catch (error) {
        toast.update(toastId, {
            render: (
                <ToastErrorMessage header={'Failed to Download Tasklists'} errorMessage={error?.message} />
            ),
            type: toast.TYPE.ERROR,
            autoClose: false
        });
        Logger.error(`Failed to download Tasklists CSV: ${error?.message}`);
    }
};
